import { collection, query, where, getDocs, Timestamp, doc, getDoc, deleteDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config';
import type { OrganizationMember } from '@/types/organization';
import type { User } from '@/types';

export async function addMemberToOrganization(
  organizationId: string,
  userId: string,
  role: 'manager' | 'customer' | 'examiner' | 'instructor',
  addedBy: string
) {
  try {
    const memberId = `${organizationId}_${userId}_${role}`;
    const memberRef = doc(collection(db, 'organizationMembers'), memberId);
    const existing = await getDoc(memberRef);
    if (existing.exists()) {
      await setDoc(memberRef, { role, addedBy }, { merge: true });
    } else {
      await setDoc(memberRef, {
        organizationId,
        userId,
        role,
        addedBy,
        addedAt: Timestamp.now()
      });
    }
    return true;
  } catch (error) {
    console.error('Error adding member to organization:', error);
    throw error;
  }
}

export async function getOrganizationMembers(organizationId: string) {
  try {
    const membersQuery = query(
      collection(db, 'organizationMembers'),
      where('organizationId', '==', organizationId)
    );
    const snapshot = await getDocs(membersQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      addedAt: doc.data().addedAt?.toDate()
    })) as OrganizationMember[];
  } catch (error) {
    console.error('Error fetching organization members:', error);
    throw error;
  }
}

export async function getMemberDetails(userId: string): Promise<User | null> {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return null;
    return {
      id: userDoc.id,
      ...userDoc.data()
    } as User;
  } catch (error) {
    console.error('Error fetching member details:', error);
    return null;
  }
}

export async function removeMemberFromOrganization(organizationId: string, userId: string, role?: 'manager' | 'customer' | 'examiner' | 'instructor') {
  try {
    // Use deterministic ID first (with role if provided)
    const memberId = role ? `${organizationId}_${userId}_${role}` : `${organizationId}_${userId}`;
    const memberRef = doc(collection(db, 'organizationMembers'), memberId);
    const existing = await getDoc(memberRef);
    if (existing.exists()) {
      await deleteDoc(memberRef);
      return true;
    }

    // Fallback: scan and delete any stray duplicates
    const membersQuery = query(
      collection(db, 'organizationMembers'),
      where('organizationId', '==', organizationId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(membersQuery);
    if (snapshot.empty) {
      console.warn('No member found to remove');
      return false;
    }
    // If role specified, only delete that role; else delete all
    if (role) {
      const toDelete = snapshot.docs.filter(d => d.data().role === role);
      if (toDelete.length === 0) return false;
      await Promise.all(toDelete.map(d => deleteDoc(d.ref)));
    } else {
      await Promise.all(snapshot.docs.map(d => deleteDoc(d.ref)));
    }
    return true;
  } catch (error) {
    console.error('Error removing member from organization:', error);
    throw error;
  }
}

/**
 * Remove membership and delete copied default habits for that org
 */
export async function removeMemberAndCleanupHabits(organizationId: string, userId: string, role?: 'manager' | 'customer' | 'examiner' | 'instructor') {
  const { deleteUserOrganizationDefaultHabitsForOrg } = await import('../habits');
  const removed = await removeMemberFromOrganization(organizationId, userId, role);
  if (removed) {
    await deleteUserOrganizationDefaultHabitsForOrg(userId, organizationId);
  }
  return removed;
}